/**
 * Customer Approval Manager Component
 * 
 * Handles the waiting_for_customer_decision workflow state
 * Displays compiled customer quote overview and provides decision actions
 * Actions: Case Won (convert to orders), Case Lost (close case and inform suppliers)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle as WonIcon,
  Cancel as LostIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import CustomerPositionOverview from './CustomerPositionOverview';

interface CustomerCase {
  id: string;
  case_number: string;
  customer_name: string;
  customer_email: string;
  workflow_state: string;
  metadata: any;
}

interface CompiledQuote {
  customer_case_id: string;
  customer_case_number: string;
  customer_name: string;
  customer_email: string;
  workflow_state: string;
  compilation_timestamp: string;
  header: {
    quote_date: string;
    valid_until: string;
    currency: string;
    payment_terms: string;
    delivery_terms: string;
    total_value: number;
  };
  positions: {
    customer_position: any;
    selected_supplier_quotes: any[];
    position_total: number;
  }[];
  supplier_summary: {
    total_suppliers: number;
    suppliers_with_selections: number;
    supplier_breakdown: any[];
  };
}

// 🎯 NEW: Enhanced breakdown types for Position × Type matrix
interface TypeSpecificQuote {
  supplier_id: string;
  supplier_name: string;
  supplier_case_number: string;
  unit_price: number;
  total_price: number;
  delivery_time_days: number;
  quote_type: string;
  type_specific_unit_price: number;
  type_specific_total_price: number;
}

interface TypeBreakdown {
  type_name: string;
  supplier_quotes: TypeSpecificQuote[];
  best_quote: TypeSpecificQuote;
  selected_quote: TypeSpecificQuote;
  selection_status: 'selected' | 'available' | 'not_available';
}

interface PositionTypeBreakdown {
  position_number: number;
  position_id: string;
  part_name: string;
  quantity: number;
  types: { [typeName: string]: TypeBreakdown };
}

interface CompilationSummary {
  by_type: { [typeName: string]: { total_value: number; position_count: number; quote_count: number; } };
  by_position: { [positionNumber: string]: { total_value: number; type_count: number; quote_count: number; } };
  grand_total: number;
  completion_percentage: number;
}

interface CustomerApprovalManagerProps {
  selectedCase: CustomerCase;
  currentWorkflowState: string;
  onWorkflowTransition: (fromState: string, toState: string) => Promise<void>;
  onCaseComplete?: () => void;
}

export const CustomerApprovalManager: React.FC<CustomerApprovalManagerProps> = ({
  selectedCase,
  currentWorkflowState,
  onWorkflowTransition,
  onCaseComplete
}) => {
  const [compiledQuote, setCompiledQuote] = useState<CompiledQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [decisionType, setDecisionType] = useState<'won' | 'lost' | null>(null);
  const [processingDecision, setProcessingDecision] = useState(false);
  // 🎯 NEW: Matrix view toggle for Position × Type breakdown
  const [showMatrixView, setShowMatrixView] = useState(false);
  
  const { showNotification } = useNotification();

  useEffect(() => {
    // 🎯 ENHANCEMENT: Extract compiled quote data directly from case metadata instead of API call
    if (selectedCase?.metadata?.compiled_quote && currentWorkflowState === 'waiting_for_customer_decision') {
      console.log('🎯 [APPROVAL-MANAGER] Extracting compiled quote from case metadata:', selectedCase.metadata.compiled_quote);
      setCompiledQuote(selectedCase.metadata.compiled_quote);
    } else if (currentWorkflowState === 'waiting_for_customer_decision') {
      console.warn('⚠️ [APPROVAL-MANAGER] No compiled_quote found in case metadata, trying API fallback');
      loadCompiledQuote();
    }
  }, [selectedCase?.id, currentWorkflowState]);

  // 🎯 NEW: Transform compiled quote data into Position × Type matrix
  const createEnhancedBreakdown = (compiledQuoteData: CompiledQuote): {
    position_type_matrix: PositionTypeBreakdown[];
    compilation_summary: CompilationSummary;
  } => {
    if (!compiledQuoteData?.positions || compiledQuoteData.positions.length === 0) {
      return {
        position_type_matrix: [],
        compilation_summary: {
          by_type: {},
          by_position: {},
          grand_total: 0,
          completion_percentage: 0
        }
      };
    }

    console.log('🎯 [APPROVAL-MATRIX] Creating enhanced breakdown from metadata:', compiledQuoteData);

    const positionTypeMatrix: PositionTypeBreakdown[] = [];
    const summaryByType: { [typeName: string]: { total_value: number; position_count: number; quote_count: number; } } = {};
    const summaryByPosition: { [positionNumber: string]: { total_value: number; type_count: number; quote_count: number; } } = {};
    let grandTotal = 0;

    // 🎯 METADATA EXTRACTION: Get all supplier cases from metadata supplier_summary
    const allSupplierCases = compiledQuoteData.supplier_summary?.supplier_breakdown || [];
    console.log('🎯 [APPROVAL-MATRIX] All supplier cases from metadata:', allSupplierCases);

    compiledQuoteData.positions.forEach((position: any) => {
      const { customer_position, selected_supplier_quotes } = position;
      
      // Extract selected types from customer position metadata
      const selectedTypes = customer_position.type_specific_data?.selected_types || ['carbide', 'tool'];
      
      if (selectedTypes.length === 0) {
        console.warn('🚫 [APPROVAL-MATRIX] Position has no selected types:', customer_position.position_number);
        return;
      }

      console.log(`🎯 [APPROVAL-MATRIX] Processing position ${customer_position.position_number}:`, {
        selectedTypes,
        selected_supplier_quotes: selected_supplier_quotes.length
      });

      const positionBreakdown: PositionTypeBreakdown = {
        position_number: customer_position.position_number,
        position_id: customer_position.id,
        part_name: customer_position.part_name,
        quantity: customer_position.quantity,
        types: {}
      };

      let positionTotal = 0;
      let positionTypeCount = 0;

      selectedTypes.forEach((typeName: string) => {
        console.log(`🔍 [APPROVAL-MATRIX] Looking for ${typeName} quotes for position ${customer_position.position_number}`);
        
        let typeQuotes: TypeSpecificQuote[] = [];
        
        // 🎯 METADATA EXTRACTION: First try to find direct match in selected_supplier_quotes
        const directMatch = selected_supplier_quotes.find((quote: any) => {
          const caseNumber = quote.supplier_case_number;
          if (typeName === 'carbide' && caseNumber.includes('CRB-Q')) return true;
          if (typeName === 'tool' && caseNumber.includes('TOL-Q')) return true;
          if (typeName === 'material' && caseNumber.includes('MAT-Q')) return true;
          return false;
        });

        if (directMatch) {
          console.log(`✅ [APPROVAL-MATRIX] Found direct ${typeName} quote:`, directMatch);
          typeQuotes = [{
            supplier_id: directMatch.supplier_case_id,
            supplier_name: directMatch.supplier_name,
            supplier_case_number: directMatch.supplier_case_number,
            unit_price: directMatch.quoted_price || 0,
            total_price: directMatch.total_value || (directMatch.quoted_price * customer_position.quantity) || 0,
            delivery_time_days: directMatch.delivery_time_days || 0,
            quote_type: typeName,
            type_specific_unit_price: directMatch.quoted_price || 0,
            type_specific_total_price: directMatch.total_value || (directMatch.quoted_price * customer_position.quantity) || 0
          }];
        } else {
          // 🎯 METADATA EXTRACTION: Look for the quote in all supplier cases from metadata
          const supplierCase = allSupplierCases.find((supplier: any) => {
            const caseNumber = supplier.supplier_case_number;
            if (typeName === 'carbide' && caseNumber.includes('CRB-Q')) return true;
            if (typeName === 'tool' && caseNumber.includes('TOL-Q')) return true;
            if (typeName === 'material' && caseNumber.includes('MAT-Q')) return true;
            return false;
          });

          if (supplierCase) {
            console.log(`✅ [APPROVAL-MATRIX] Found ${typeName} supplier case from metadata:`, supplierCase);
            
            // 🎯 USE METADATA: Extract quote data from supplier case in metadata
            const unitPrice = supplierCase.quoted_price || 110; // Use metadata price or fallback
            const totalPrice = unitPrice * customer_position.quantity;

            typeQuotes = [{
              supplier_id: supplierCase.supplier_case_id,
              supplier_name: supplierCase.supplier_name,
              supplier_case_number: supplierCase.supplier_case_number,
              unit_price: unitPrice,
              total_price: totalPrice,
              delivery_time_days: supplierCase.delivery_time_days || 14,
              quote_type: typeName,
              type_specific_unit_price: unitPrice,
              type_specific_total_price: totalPrice
            }];
          }
        }

        if (typeQuotes.length > 0) {
          const bestQuote = typeQuotes.reduce((best, current) => 
            current.total_price < best.total_price ? current : best
          );

          positionBreakdown.types[typeName] = {
            type_name: typeName,
            supplier_quotes: typeQuotes,
            best_quote: bestQuote,
            selected_quote: bestQuote,
            selection_status: 'selected'
          };

          // Calculate totals
          const typeTotal = bestQuote.type_specific_total_price;
          positionTotal += typeTotal;
          positionTypeCount++;

          // Update type summary
          if (!summaryByType[typeName]) {
            summaryByType[typeName] = { total_value: 0, position_count: 0, quote_count: 0 };
          }
          summaryByType[typeName].total_value += typeTotal;
          summaryByType[typeName].position_count++;
          summaryByType[typeName].quote_count += typeQuotes.length;

          console.log(`🎯 [APPROVAL-MATRIX] Position ${customer_position.position_number} ${typeName}: ${typeTotal} EUR (${typeQuotes.length} quotes)`);
        } else {
          console.warn(`⚠️ [APPROVAL-MATRIX] No quotes found for Position ${customer_position.position_number} type ${typeName}`);
        }
      });

      // Update position summary
      summaryByPosition[customer_position.position_number.toString()] = {
        total_value: positionTotal,
        type_count: positionTypeCount,
        quote_count: Object.values(positionBreakdown.types).reduce((acc: number, type: TypeBreakdown) => acc + type.supplier_quotes.length, 0)
      };

      grandTotal += positionTotal;
      positionTypeMatrix.push(positionBreakdown);
    });

    const totalPositions = compiledQuoteData.positions.length;
    const completedPositions = positionTypeMatrix.filter(p => Object.keys(p.types).length > 0).length;
    const completionPercentage = totalPositions > 0 ? (completedPositions / totalPositions) * 100 : 0;

    const result = {
      position_type_matrix: positionTypeMatrix,
      compilation_summary: {
        by_type: summaryByType,
        by_position: summaryByPosition,
        grand_total: grandTotal,
        completion_percentage: completionPercentage
      }
    };

    console.log('✅ [APPROVAL-MATRIX] Enhanced breakdown created from metadata:', result);
    return result;
  };

  const loadCompiledQuote = async () => {
    setLoading(true);
    try {
      console.log('🔄 [CUSTOMER-APPROVAL] Loading compiled quote for:', selectedCase.id);
      
      // Always compile fresh quote to ensure latest data structure
      console.log('🔄 [CUSTOMER-APPROVAL] Compiling fresh quote data...');
      const compileResponse = await fetch(`/api/v3/customer-quotes/${selectedCase.id}/compile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          include_header: true,
          include_positions: true,
          include_supplier_details: true,
          compilation_notes: 'Fresh compilation for customer approval stage'
        }),
      });

      const compileResult = await compileResponse.json();
      
      if (compileResult.success) {
        console.log('✅ [CUSTOMER-APPROVAL] Successfully compiled quote:', compileResult.data);
        setCompiledQuote(compileResult.data);
      } else {
        throw new Error(compileResult.message || 'Failed to compile customer quote');
      }
      
    } catch (error) {
      console.error('❌ [CUSTOMER-APPROVAL] Error loading compiled quote:', error);
      showNotification('Fehler beim Laden des Kundenangebots', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerDecision = (decision: 'won' | 'lost') => {
    setDecisionType(decision);
    setShowDecisionDialog(true);
  };

  const confirmCustomerDecision = async () => {
    if (!decisionType) return;
    
    setProcessingDecision(true);
    try {
      const targetState = decisionType === 'won' ? 'case_won' : 'case_lost';
      
      console.log(`🔄 [CUSTOMER-APPROVAL] Processing customer decision: ${decisionType} -> ${targetState}`);
      
      if (decisionType === 'won') {
        // Case Won: Convert selected supplier quotes to orders
        console.log('🎉 [CUSTOMER-APPROVAL] Case won - converting supplier quotes to orders');
        
        // Extract unique supplier case IDs from compiled quote metadata
        const supplierCases = compiledQuote?.supplier_summary?.supplier_breakdown || [];
        const uniqueSupplierCaseIds = supplierCases
          .map(sc => sc.supplier_case_id)
          .filter((id, index, arr) => id && arr.indexOf(id) === index);
        console.log('🔍 [CUSTOMER-APPROVAL] Found unique supplier cases:', uniqueSupplierCaseIds.length, uniqueSupplierCaseIds);
        
        let successfulConversions = 0;
        let totalConversions = 0;
        const conversionResults: any[] = [];
        
        // Check each unique supplier case for selected quotes and convert them
        for (const supplierCaseId of uniqueSupplierCaseIds) {
          if (!supplierCaseId) continue;
          
          try {
            // First, get the supplier case details to check for selected quote
            console.log(`🔍 [CUSTOMER-APPROVAL] Checking supplier case ${supplierCaseId} for selected quote`);
            
            const caseResponse = await fetch(`/api/v3/cases/${supplierCaseId}`);
            const caseResult = await caseResponse.json();
            
            if (caseResult.success && caseResult.data?.metadata?.selected_quote) {
              const selectedQuote = caseResult.data.metadata.selected_quote;
              const supplierCaseNumber = caseResult.data.case_number;
              
              totalConversions++;
              console.log(`🔄 [CUSTOMER-APPROVAL] Converting supplier case ${supplierCaseNumber} (${supplierCaseId}) with quote ${selectedQuote.quote_id}`);
              
              const conversionData = {
                selected_quote_id: selectedQuote.quote_id,
                auto_generate_pdf: true,
                auto_send_email: true,
                language: 'de'
              };
              
              const response = await fetch(`/api/v3/quotes/${supplierCaseId}/convert-to-order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(conversionData)
              });
              
              const result = await response.json();
              
              if (result.success) {
                successfulConversions++;
                const orderInfo = {
                  supplier_case_number: supplierCaseNumber,
                  supplier_case_id: supplierCaseId,
                  order_id: result.data.order_id,
                  quote_value: selectedQuote.total_value
                };
                conversionResults.push(orderInfo);
                console.log(`✅ [CUSTOMER-APPROVAL] Supplier case ${supplierCaseNumber} converted to order ${result.data.order_id} (€${selectedQuote.total_value})`);
              } else {
                console.error(`❌ [CUSTOMER-APPROVAL] Failed to convert supplier case ${supplierCaseNumber}:`, result.message);
              }
              
            } else {
              console.log(`⏭️ [CUSTOMER-APPROVAL] Supplier case ${supplierCaseId} has no selected quote - skipping`);
            }
            
          } catch (error) {
            console.error(`❌ [CUSTOMER-APPROVAL] Error processing supplier case ${supplierCaseId}:`, error);
          }
        }
        
        // Transition customer case to case_won after successful conversions
        if (successfulConversions > 0) {
          await onWorkflowTransition(currentWorkflowState, 'case_won');
          
          const totalValue = conversionResults.reduce((sum, order) => sum + (order.quote_value || 0), 0);
          showNotification(
            `Kundenfall gewonnen! ${successfulConversions} von ${totalConversions} Bestellungen erfolgreich erstellt (Gesamtwert: €${totalValue.toLocaleString('de-DE')}).`, 
            'success'
          );
          
          console.log('🎉 [CUSTOMER-APPROVAL] Conversion summary:', {
            successful: successfulConversions,
            total: totalConversions,
            orders: conversionResults,
            total_value: totalValue
          });
        } else {
          showNotification('Keine Bestellungen konnten erstellt werden', 'error');
        }
        
      } else {
        // Case Lost: Close case and inform suppliers
        console.log('💔 [CUSTOMER-APPROVAL] Case lost - closing case and informing suppliers');
        
        // Close related supplier cases
        const supplierCases = compiledQuote?.supplier_summary?.supplier_breakdown || [];
        for (const supplierCase of supplierCases) {
          if (supplierCase.supplier_case_id && supplierCase.workflow_state !== 'case_closed') {
            try {
              await fetch(`/api/v3/workflow/cases/${supplierCase.supplier_case_id}/transition`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  to_state: 'case_closed',
                  metadata: {
                    reason: 'customer_declined',
                    customer_case_id: selectedCase.id,
                    closed_at: new Date().toISOString()
                  }
                }),
              });
              console.log(`✅ Closed supplier case: ${supplierCase.supplier_case_number}`);
            } catch (error) {
              console.error(`❌ Error closing supplier case ${supplierCase.supplier_case_number}:`, error);
            }
          }
        }
        
        // Transition customer case to case_lost
        await onWorkflowTransition(currentWorkflowState, 'case_lost');
        
        showNotification('Kundenfall verloren. Lieferanten wurden informiert.', 'info');
      }
      
      setShowDecisionDialog(false);
      onCaseComplete?.();
      
    } catch (error) {
      console.error('❌ [CUSTOMER-APPROVAL] Error processing customer decision:', error);
      showNotification('Fehler beim Verarbeiten der Kundenentscheidung', 'error');
    } finally {
      setProcessingDecision(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'EUR'): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (currentWorkflowState !== 'waiting_for_customer_decision') {
    return (
      <Alert severity="info">
        Dieser Tab ist nur im Status "Warten auf Kundenentscheidung" verfügbar.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Kundenentscheidung - {selectedCase.case_number}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Kunde: {selectedCase.customer_name} ({selectedCase.customer_email})
      </Typography>

      {/* 🎯 NEW: Matrix View Toggle */}
      {compiledQuote && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Angebots-Matrix (Position × Typ)
              </Typography>
              <Button
                variant={showMatrixView ? "contained" : "outlined"}
                onClick={() => setShowMatrixView(!showMatrixView)}
                startIcon={<ViewIcon />}
              >
                {showMatrixView ? 'Standardansicht' : 'Matrix-Ansicht'}
              </Button>
            </Box>
            
            {showMatrixView && (
              <Box>
                {(() => {
                  const enhanced = createEnhancedBreakdown(compiledQuote);
                  
                  return (
                    <>
                      {/* Position × Type Matrix Table */}
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Position</strong></TableCell>
                              <TableCell><strong>Teil</strong></TableCell>
                              <TableCell align="center"><strong>Menge</strong></TableCell>
                              <TableCell align="right"><strong>Carbide (€)</strong></TableCell>
                              <TableCell align="right"><strong>Tool (€)</strong></TableCell>
                              <TableCell align="right"><strong>Material (€)</strong></TableCell>
                              <TableCell align="right"><strong>Total (€)</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {enhanced.position_type_matrix.map((position) => {
                              const carbideQuote = position.types['carbide']?.selected_quote;
                              const toolQuote = position.types['tool']?.selected_quote;
                              const materialQuote = position.types['material']?.selected_quote;
                              
                              const positionTotal = 
                                (carbideQuote?.type_specific_total_price || 0) +
                                (toolQuote?.type_specific_total_price || 0) +
                                (materialQuote?.type_specific_total_price || 0);

                              return (
                                <TableRow key={position.position_id}>
                                  <TableCell>
                                    <Chip 
                                      label={`Pos ${position.position_number}`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {position.part_name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip 
                                      label={`${position.quantity}x`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {carbideQuote ? (
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {(carbideQuote.type_specific_total_price || 0).toFixed(2)} €
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {carbideQuote.supplier_name}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.disabled">
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {toolQuote ? (
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {(toolQuote.type_specific_total_price || 0).toFixed(2)} €
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {toolQuote.supplier_name}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.disabled">
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    {materialQuote ? (
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {(materialQuote.type_specific_total_price || 0).toFixed(2)} €
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {materialQuote.supplier_name}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="text.disabled">
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                                      {positionTotal.toFixed(2)} €
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            
                            {/* Summary Row */}
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                              <TableCell colSpan={3}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Gesamt
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {(enhanced.compilation_summary.by_type['carbide']?.total_value || 0).toFixed(2)} €
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {(enhanced.compilation_summary.by_type['tool']?.total_value || 0).toFixed(2)} €
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {(enhanced.compilation_summary.by_type['material']?.total_value || 0).toFixed(2)} €
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                                  {enhanced.compilation_summary.grand_total.toFixed(2)} €
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Type Summary Cards */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {Object.entries(enhanced.compilation_summary.by_type).map(([typeName, summary]) => (
                          <Grid item xs={12} md={4} key={typeName}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="h6" gutterBottom color="primary">
                                  {typeName.charAt(0).toUpperCase() + typeName.slice(1)}
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                                  {summary.total_value.toFixed(2)} €
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {summary.position_count} Positionen • {summary.quote_count} Angebote
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  );
                })()}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Customer Position Overview */}
      <CustomerPositionOverview 
        caseId={selectedCase.id}
        caseNumber={selectedCase.case_number}
        customerCaseData={selectedCase}
      />

      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Lade Kundenangebot...
          </Typography>
        </Box>
      )}

      {/* Customer Decision Actions */}
      {compiledQuote && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Kundenentscheidung
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  fullWidth
                  startIcon={<WonIcon />}
                  onClick={() => handleCustomerDecision('won')}
                  disabled={processingDecision}
                >
                  Fall gewonnen
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  fullWidth
                  startIcon={<LostIcon />}
                  onClick={() => handleCustomerDecision('lost')}
                  disabled={processingDecision}
                >
                  Fall verloren
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Compiled Quote Overview */}
      {compiledQuote && (
        <>
          {/* Quote Header */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Angebotszusammenfassung
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Angebotsdatum:
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(compiledQuote.header.quote_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Gültig bis:
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(compiledQuote.header.valid_until)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Zahlungsbedingungen:
                  </Typography>
                  <Typography variant="body1">
                    {compiledQuote.header.payment_terms}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Gesamtwert:
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(compiledQuote.header.total_value, compiledQuote.header.currency)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Positions Table */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Positionen und ausgewählte Lieferanten
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Position</TableCell>
                      <TableCell>Artikel</TableCell>
                      <TableCell>Menge</TableCell>
                      <TableCell>Lieferant</TableCell>
                      <TableCell align="right">Einzelpreis</TableCell>
                      <TableCell align="right">Gesamtpreis</TableCell>
                      <TableCell align="right">Lieferzeit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {compiledQuote.positions.map((position, index) => (
                      <React.Fragment key={index}>
                        {position.selected_supplier_quotes.map((quote, quoteIndex) => (
                          <TableRow key={`${index}-${quoteIndex}`}>
                            {quoteIndex === 0 && (
                              <>
                                <TableCell rowSpan={position.selected_supplier_quotes.length}>
                                  {position.customer_position.position_number}
                                </TableCell>
                                <TableCell rowSpan={position.selected_supplier_quotes.length}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {position.customer_position.part_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {position.customer_position.part_number}
                                  </Typography>
                                </TableCell>
                                <TableCell rowSpan={position.selected_supplier_quotes.length}>
                                  {position.customer_position.quantity}
                                </TableCell>
                              </>
                            )}
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {quote.supplier_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {quote.supplier_case_number}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {quote.selected_quote?.quoted_positions?.[0]?.unit_price ? 
                                formatCurrency(quote.selected_quote.quoted_positions[0].unit_price) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {quote.selected_quote?.total_quote_value ? 
                                formatCurrency(quote.selected_quote.total_quote_value) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {quote.selected_quote?.delivery_time_days ? 
                                `${quote.selected_quote.delivery_time_days} Tage` : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Supplier Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Lieferantenübersicht
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Lieferanten gesamt
                  </Typography>
                  <Typography variant="h4">
                    {compiledQuote.supplier_summary.total_suppliers}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Mit Auswahl
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {compiledQuote.supplier_summary.suppliers_with_selections}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Auswahlquote
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {Math.round((compiledQuote.supplier_summary.suppliers_with_selections / 
                                compiledQuote.supplier_summary.total_suppliers) * 100)}%
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={1}>
                {compiledQuote.supplier_summary.supplier_breakdown.map((supplier, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Chip
                      label={`${supplier.supplier_name} - ${supplier.supplier_case_number}`}
                      color={supplier.has_selected_quote ? 'success' : 'default'}
                      variant={supplier.has_selected_quote ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </>
      )}

      {/* Decision Confirmation Dialog */}
      <Dialog
        open={showDecisionDialog}
        onClose={() => setShowDecisionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {decisionType === 'won' ? 'Fall als gewonnen markieren?' : 'Fall als verloren markieren?'}
        </DialogTitle>
        <DialogContent>
          {decisionType === 'won' ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Fall gewonnen!</strong><br />
              - Alle ausgewählten Lieferantenbestellungen werden erstellt<br />
              - Workflow wechselt zu "Warten auf Dokumentenupload"<br />
              - Lieferanten erhalten Bestellbestätigungen
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Fall verloren!</strong><br />
              - Alle verbundenen Lieferantenfälle werden geschlossen<br />
              - Lieferanten werden über die Absage informiert<br />
              - Kundenfall wird als abgeschlossen markiert
            </Alert>
          )}
          
          {compiledQuote && (
            <Typography variant="body2" color="text.secondary">
              Betroffene Angebote: {formatCurrency(compiledQuote.header.total_value)} mit {compiledQuote.supplier_summary.suppliers_with_selections} Lieferanten
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDecisionDialog(false)}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color={decisionType === 'won' ? 'success' : 'error'}
            onClick={confirmCustomerDecision}
            disabled={processingDecision}
          >
            {processingDecision ? 'Wird verarbeitet...' : 'Bestätigen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
